import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { CellClassParams } from 'ag-grid-community';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { BehaviorSubject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FormlyFieldBaseComponent } from '../formly-field-base.component';
import { AggFunc } from '../../../constants';
import { WarpWeftPlan, WarpWeftPlanRepeat } from '../../../models';
import { ModalService, QualityService, UtilityService } from '../../../services';
import { GridService } from '../../../services/component-services/grid.service';
import { FormlyService } from '../../../services/component-services/formly.service';
import { BaseInlineFormComponent } from '../../base-inline-form/base-inline-form.component';
import { BaseFormComponent } from '../../base-form/base-form.component';
import { ignoreModalError } from '../../../helpers';
import { GridComponent } from '../../grids/grid/grid.component';
import { FormOkCancelModalComponent } from '../../modals/form-ok-cancel-modal/form-ok-cancel-modal.component';

@Component({
  selector: 'app-formly-warp-weft-plan',
  templateUrl: './formly-warp-weft-plan.component.html',
  styleUrl: './formly-warp-weft-plan.component.scss',
  imports: [BaseInlineFormComponent, GridComponent, BaseFormComponent, AsyncPipe],
})
export class FormlyWarpWeftPlanComponent extends FormlyFieldBaseComponent implements OnInit, OnDestroy {
  private readonly gridService = inject(GridService);
  private readonly formlyService = inject(FormlyService);
  private readonly modalService = inject(ModalService);
  private readonly utilityService = inject(UtilityService);
  private readonly qualityService = inject(QualityService);

  private readonly grid = viewChild(GridComponent);

  protected maxColumns = 50;

  rowData$ = new BehaviorSubject<any[]>([
    ...this.utilityService.array1ToN(12).map((index) => ({ letter: this.getRowLetter(index), row: index })),
    {},
    {},
  ]);

  protected columnDefs: ColDef[] = [
    this.gridService.calculation('threads', $localize`Threads`, (model) => this.countThreads(model), {
      aggFunc: AggFunc.sumInteger,
      width: 70,
      pinned: 'left',
    }),
    this.gridService.calculation('extras', $localize`Extras`, (model) => this.countExtras(model), {
      aggFunc: AggFunc.sumInteger,
      width: 70,
      pinned: 'left',
    }),
    this.gridService.text('letter', '', { width: 35, cellClass: 'fw-bold', pinned: 'left' }),
    ...this.utilityService.array1ToN(this.maxColumns).map((index) =>
      this.gridService.editableField(
        index.toString(),
        index.toString(),
        (model) => this.edit(model),
        (model) => !this.props.editable || this.props.editable(this.model, model.row),
        { width: 50 },
        (cellClassParams: CellClassParams) => this.getCellColour(cellClassParams),
        /(^[0-9]+$)|(^\*$)|(^$)/, // only allow numbers, '*' or empty string
      ),
    ),
  ];

  private addBracketFields: FormlyFieldConfig[] = [
    this.formlyService.number('repeat', 'Repeat Count', {
      required: true,
      min: 2,
    }),
  ];

  protected loaded = false;
  private subscription: Subscription;
  protected buttons: FormlyFieldConfig[];
  protected fields: FormlyFieldConfig[];
  protected fieldModel: any = {};

  ngOnInit(): void {
    this.reset();

    this.rowData$.pipe(takeUntil(this.destroy$)).subscribe((rowData) => this.grid()?.gridApi.setRowData(rowData));

    this.qualityService.qualitySelected$.pipe(takeUntil(this.destroy$)).subscribe((id) => this.updateQuality(id));

    this.subscription = this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((model) => {
      if (this.loaded) {
        return;
      }
      if (model[this.key] && model[this.planRepeatsProperty]) {
        this.loaded = true;
        this.clearSubscription();
        this.fieldModel.code = this.model.code;
        this.populatePlans(model[this.key]);
        this.populatePlanRepeats(model[this.planRepeatsProperty]);
        this.gridToCode();
      }
    });

    this.getFields();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.clearSubscription();
  }

  getRowLetter(row: number): string {
    return String.fromCharCode(row + 64);
  }

  getLetterRow(letter: string): number {
    return letter.charCodeAt(0) - 64;
  }

  edit(model: any) {
    // model format: { letter: 'A', row: 1, 1: '4', 2: '3' }
    let plan = this.plan;
    const cells: WarpWeftPlan[] = [];
    const row = model.row;
    for (const prop in model) {
      switch (prop) {
        case 'letter':
        case 'row':
        case 'repeatLevel':
          break;
        default:
          const cell: WarpWeftPlan = {
            row,
          } as WarpWeftPlan;

          cell.column = +prop;

          const value = model[prop];
          if (value === '*') {
            cell.extra = value;
          } else {
            cell.number = +value;
          }

          cells.push(cell);
          break;
      }
    }
    for (const cell of cells) {
      let found = false;
      for (const p of plan) {
        if (p.column === cell.column && p.row === cell.row) {
          if (cell.number || cell.extra) {
            p.number = cell.number;
            p.extra = cell.extra;
          } else {
            plan = plan.filter((x) => x.row !== cell.row || x.column !== cell.column);
          }
          found = true;
          break;
        }
      }
      if (!found) {
        plan.push(cell);
      }
    }

    plan = this.utilityService.sort(
      plan,
      (x) => x.column,
      (x) => x.row,
    );
    this.form.patchValue({
      [this.key]: plan,
    } as any);
    this.populatePlans(plan);
    this.gridToCode();
    this.setEdited();
  }

  private get planRepeatsProperty() {
    return this.key + 'Repeats';
  }

  private get plan(): WarpWeftPlan[] {
    return this.model[this.key] as WarpWeftPlan[];
  }

  private get planRepeats(): WarpWeftPlanRepeat[] {
    return this.model[this.planRepeatsProperty] as WarpWeftPlanRepeat[];
  }

  private getCellColour(params: CellClassParams) {
    if (params.data.isAggregate) {
      return undefined;
    }
    const planRepeats = this.planRepeats;
    const columnNumber = +params.column.getId();
    const level1s = planRepeats.filter((x) => x.level === 1);
    const level2s = planRepeats.filter((x) => x.level === 2);
    const level1 = level1s.find((x) => columnNumber >= x.startColumn && columnNumber <= x.endColumn);
    const level2 = level2s.find((x) => columnNumber >= x.startColumn && columnNumber <= x.endColumn);
    if (level2 && params.rowIndex === this.rowData$.value.length - 1) {
      return { backgroundColor: level2s.indexOf(level2) % 2 === 0 ? 'orange' : 'lightblue' };
    }
    if (level1) {
      return { backgroundColor: level1s.indexOf(level1) % 2 === 0 ? 'yellow' : 'lightgreen' };
    }
    return undefined;
  }

  private setEdited() {
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    for (const prop in this.form.controls) {
      this.form.controls[prop].updateValueAndValidity();
    }
    this.calculate();
  }

  private reset() {
    if (this.key && !this.model[this.key]) {
      this.form.patchValue({ [this.key]: undefined } as any);
    }
  }

  private getFields() {
    this.buttons = [
      this.formlyService.button($localize`Add Bracket`, () => this.addBracket()),
      this.formlyService.button($localize`Remove Bracket`, () => this.removeBracket()),
      this.formlyService.button($localize`Clear Brackets`, () => this.clearBrackets()),
      this.formlyService.button($localize`Clear Plan`, () => this.clearPlan()),
    ];
    this.fields = [
      this.formlyService.text('code', $localize`Code`, {
        change: (field: FormlyFieldConfig, event: any) => this.codeToGrid(event.target.value),
      }),
    ];
  }

  private clearSubscription() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  private updateQuality(id: string) {
    this.qualityService
      .edit(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((quality) => {
        this.form.patchValue({
          groundEnds: quality.groundEnds,
        } as any);
        this.calculate();
      });
  }

  private addBracket() {
    const selectedCells = this.grid().gridApi.getCellRanges()[0];
    if (!selectedCells?.columns) {
      return;
    }
    const startColumn = +selectedCells.columns[0]['colId'];
    const endColumn = +selectedCells.columns[selectedCells.columns.length - 1]['colId'];
    const planRepeats = this.planRepeats;
    const { modal, component } = this.modalService.openSmall<FormOkCancelModalComponent>(FormOkCancelModalComponent);
    component.header.set($localize`Add Bracket`);
    component.fields.set(this.addBracketFields);
    modal.result.then((model) => {
      const existingRepeats = planRepeats.filter((x) => x.startColumn >= startColumn && x.endColumn <= endColumn);
      if (existingRepeats.length === 0 || existingRepeats.filter((x) => x.level !== 1).length === 0) {
        planRepeats.push({
          startColumn,
          endColumn,
          level: existingRepeats.length ? 2 : 1,
          repeat: +model.repeat,
        });

        this.form.patchValue({
          [this.planRepeatsProperty]: planRepeats,
        } as any);
        this.populatePlanRepeats(planRepeats);
        this.gridToCode();
        this.setEdited();
      }
    }, ignoreModalError);
  }

  private removeBracket() {
    const selectedCells = this.grid().gridApi.getCellRanges()[0];
    if (!selectedCells?.columns) {
      return;
    }
    const startColumn = +selectedCells.columns[0]['colId'];
    const endColumn = +selectedCells.columns[selectedCells.columns.length - 1]['colId'];
    let planRepeats = this.planRepeats;
    planRepeats = planRepeats.filter((x) => !(x.startColumn >= startColumn && x.endColumn <= endColumn));
    this.form.patchValue({
      [this.planRepeatsProperty]: planRepeats,
    } as any);
    this.populatePlanRepeats(planRepeats);
    this.gridToCode();
    this.setEdited();
  }

  private clearBrackets() {
    const planRepeats = [];
    this.form.patchValue({
      [this.planRepeatsProperty]: planRepeats,
    } as any);
    this.populatePlanRepeats(planRepeats);
    this.gridToCode();
    this.setEdited();
  }

  private clearPlan() {
    this.clearGrid();

    const plans = [];
    const planRepeats = [];
    this.form.patchValue({
      [this.key]: plans,
      [this.planRepeatsProperty]: planRepeats,
    } as any);
    this.populatePlans(plans);
    this.populatePlanRepeats(planRepeats);
    this.gridToCode();
    this.setEdited();
  }

  clearGrid() {
    const data = this.rowData$.value.map((row) => ({
      row: row.row,
      letter: row.letter,
      repeatLevel: row.repeatLevel,
    }));
    this.rowData$.next(data);
  }

  private populatePlans(plans: WarpWeftPlan[]) {
    const data = this.rowData$.value;
    for (const value of plans) {
      const plan = value as WarpWeftPlan;
      const row = data.find((x) => x.row === plan.row);
      if (row) {
        row[plan.column] = plan.extra ?? plan.number.toString();
      }
    }
    this.rowData$.next(data);
    this.calculate();
  }

  private populatePlanRepeats(planRepeats: WarpWeftPlanRepeat[]) {
    const data = this.rowData$.value;
    data[data.length - 1] = { repeatLevel: 2 };
    data[data.length - 2] = { repeatLevel: 1 };
    for (const value of planRepeats) {
      const planRepeat = value as WarpWeftPlanRepeat;
      const row = data.find((x) => x.repeatLevel === planRepeat.level);
      if (row) {
        row[planRepeat.startColumn] = planRepeat.repeat;
      }
    }
    this.rowData$.next(data);
    this.calculate();
  }

  private calculate() {
    const { endsPerRepeat, extrasPerRepeat } = this.countEndsAndExtras();
    const totalEndsPlusExtras = endsPerRepeat + extrasPerRepeat;
    const patternRepeats = totalEndsPlusExtras === 0 ? 0 : Math.floor(this.model.groundEnds / totalEndsPlusExtras);
    const endsOver = totalEndsPlusExtras === 0 ? 0 : this.model.groundEnds % totalEndsPlusExtras;

    let model: any = {
      endsPerRepeat,
      extrasPerRepeat,
      totalEndsPlusExtras,
      patternRepeats,
      endsOver,
    };

    if (!this.model.expectedEndsPlusExtras) {
      model = { ...model, expectedEndsPlusExtras: totalEndsPlusExtras };
    }

    this.form.patchValue(model);
  }

  private countThreads(model: any) {
    if (!model.letter) {
      return undefined;
    }
    const { endsPerRepeat } = this.countEndsAndExtras(model.row);
    return endsPerRepeat;
  }

  private countExtras(model: any) {
    if (!model.letter) {
      return undefined;
    }
    const { extrasPerRepeat } = this.countEndsAndExtras(model.row);
    return extrasPerRepeat;
  }

  countEndsAndExtras(row?: number) {
    const plan = this.plan;
    const planRepeats = this.planRepeats;
    let endsPerRepeat = 0;
    let extrasPerRepeat = 0;
    for (const cell of plan) {
      if (row && cell.row !== row) {
        continue;
      }
      let multiplier = 1;
      const repeats = planRepeats.filter((p) => cell.column >= p.startColumn && cell.column <= p.endColumn);
      for (const repeat of repeats) {
        multiplier *= repeat.repeat;
      }
      if (cell.number) {
        endsPerRepeat += cell.number * multiplier;
      }
      if (cell.extra) {
        extrasPerRepeat += multiplier;
      }
    }
    return { endsPerRepeat, extrasPerRepeat };
  }

  private gridToCode() {
    let code = '';

    for (let column = 1; column <= this.maxColumns; column++) {
      const repeatLevel1Start = this.planRepeats.find((x) => x.startColumn === column && x.level === 1);
      const repeatLevel1End = this.planRepeats.find((x) => x.endColumn === column && x.level === 1);
      const repeatLevel2Start = this.planRepeats.find((x) => x.startColumn === column && x.level === 2);
      const repeatLevel2End = this.planRepeats.find((x) => x.endColumn === column && x.level === 2);
      const columnPlans = this.utilityService.sort(
        this.plan.filter((x) => x.column === column),
        (x) => x.row,
      );
      if (repeatLevel2Start) {
        code += `${repeatLevel2Start.repeat}(`;
      }
      if (repeatLevel1Start) {
        code += `${repeatLevel1Start.repeat}(`;
      }
      for (const columnPlan of columnPlans) {
        code += `${columnPlan.extra ?? columnPlan.number}${this.getRowLetter(columnPlan.row)}`;
      }
      if (repeatLevel1End) {
        code += ')';
      }
      if (repeatLevel2End) {
        code += ')';
      }
    }

    this.fieldModel = { code };
    this.model.code = code;
    this.form.patchValue({ code } as any);
  }

  private codeToGrid(code: string) {
    code = code.toUpperCase();
    const maxLetter = this.getRowLetter(this.model.shadesPerWarp);

    let lastLetter = 'Z';
    let column = 0;
    const plans: WarpWeftPlan[] = [];
    const planRepeats: WarpWeftPlanRepeat[] = [];
    const planRepeatsStack: WarpWeftPlanRepeat[] = [];
    let i: number;
    for (i = 0; i < code.length; i++) {
      if (code[i] === ')') {
        if (!planRepeatsStack.length) {
          break;
        }
        planRepeatsStack[planRepeatsStack.length - 1].endColumn = column;
        planRepeats.push(planRepeatsStack.pop());
        lastLetter = 'Z';
        continue;
      }

      // 2A or *B or 3(
      const num = code[i];
      if (++i === code.length) {
        break;
      }
      const letter = code[i];
      const isLetter = letter >= 'A' && letter <= maxLetter;
      if (isLetter && letter <= lastLetter) {
        column++;
      }
      if (letter === '(') {
        if (isNaN(+num)) {
          break;
        }
        if (planRepeatsStack.length >= 2) {
          break;
        }
        planRepeatsStack.forEach((x) => (x.level = 2));
        planRepeatsStack.push({
          startColumn: column + 1,
          level: 1,
          repeat: +num,
        } as WarpWeftPlanRepeat);
      } else if (isLetter) {
        lastLetter = letter;
        if (num === '*') {
          plans.push({
            column,
            row: this.getLetterRow(letter),
            number: 0,
            extra: '*',
          });
        } else if (!isNaN(+num)) {
          plans.push({
            column,
            row: this.getLetterRow(letter),
            number: +num,
            extra: undefined,
          });
        } else {
          break;
        }
      } else {
        break;
      }
    }
    if (i === code.length && !planRepeatsStack.length) {
      this.form.patchValue({
        [this.key]: plans,
        [this.planRepeatsProperty]: planRepeats,
      } as any);
      this.clearGrid();
      this.populatePlans(plans);
      this.populatePlanRepeats(planRepeats);
      this.calculate();
      this.setEdited();
    }
    this.gridToCode();
  }
}
